import { useState } from 'react';
import { useBlog } from '../contexts/BlogContext';
import { BlogPost, CreatePostInput, UpdatePostInput } from '../models/Post';
import { EditBlogPostModal } from '../components/modals/EditBlogPostModal';
import { 
    DragDropContext, 
    Droppable, 
    Draggable, 
    DropResult,  
    DraggableProvided 
} from 'react-beautiful-dnd';
import { PencilIcon, TrashIcon, PlusIcon } from 'lucide-react';

const StatusBadge = ({ status }: { status: number | 'draft' | 'published' }) => {
    const isPublished = status === 1 || status === 'published';
    return (
        <span className={`inline-block px-2 py-1 rounded-full text-xs ${
            isPublished 
                ? 'bg-green-100 text-green-800' 
                : 'bg-yellow-100 text-yellow-800'
        }`}>
            {isPublished ? 'Published' : 'Draft'}
        </span>
    );
};

export default function AdminPage() {
    const { posts, loading, error, createPost, updatePost, deletePost, reorderPosts } = useBlog();
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);

    const handlePostSubmit = async (postData: CreatePostInput | UpdatePostInput) => {
        try {
            console.log('AdminPage: Submitting post data:', postData);
            
            if (isCreateModalOpen) {
                await createPost(postData as CreatePostInput);
                console.log('AdminPage: Created new post');
                setIsCreateModalOpen(false);
            } else {
                if (!selectedPost) return;
                await updatePost(selectedPost.Id, postData as UpdatePostInput);
                console.log('AdminPage: Updated post');
                setIsEditModalOpen(false);
                setSelectedPost(null);
            }
        } catch (err) {
            console.error('AdminPage: Error submitting post:', err);
        }
    };

    const handleDeletePost = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this post?')) return;
        try {
            await deletePost(id);
        } catch (err) {
            console.error('Failed to delete post:', err);
        }
    };

    const handleDragEnd = async (result: DropResult) => {
        if (!result.destination) return;

        const items = Array.from(posts);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        try {
            await reorderPosts(items.map(item => item.Id));
        } catch (err) {
            console.error('Failed to reorder posts:', err);
        }
    };

    if (loading) return <div className="flex justify-center p-8">Loading...</div>;

    return (
        <div className="max-w-6xl mx-auto p-6">
            <div className="flex justify-between items-center mb-8">
                <h1 className="text-3xl font-bold">Blog Admin</h1>
                <button 
                    className="bg-primary text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-primary/90"
                    onClick={() => setIsCreateModalOpen(true)}
                >
                    <PlusIcon size={20} />
                    New Post
                </button>
            </div>

            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    {error}
                </div>
            )}

            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="posts" type="POST">
                    {(provided) => (
                        <div 
                            {...provided.droppableProps} 
                            ref={provided.innerRef}
                            className="space-y-4"
                        >
                            {posts.map((post, index) => (
                                <Draggable 
                                    key={post.Id} 
                                    draggableId={String(post.Id)}
                                    index={index}
                                >
                                    {(provided: DraggableProvided) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            className="bg-white p-4 rounded-lg shadow hover:shadow-md transition-shadow"
                                        >
                                            <div className="flex justify-between items-start">
                                                <div className="flex-1">
                                                    <h3 className="font-bold text-lg">{post.Title}</h3>
                                                    <p className="text-gray-600 mt-2 line-clamp-2">
                                                        {post.Excerpt}
                                                    </p>
                                                    <div className="flex flex-wrap gap-2 mt-2">
                                                        {post.Tags && post.Tags.map((tag, idx) => (
                                                            <span 
                                                                key={idx}
                                                                className="bg-gray-100 px-2 py-1 rounded-full text-xs"
                                                            >
                                                                {tag}
                                                            </span>
                                                        ))}
                                                    </div>
                                                    <div className="flex items-center gap-4 mt-2">
                                                        <p className="text-gray-600 text-sm">
                                                            {new Date(post.CreatedAt).toLocaleDateString()}
                                                        </p>
                                                        <StatusBadge status={post.Status} />
                                                    </div>
                                                </div>
                                                <div className="flex gap-2 ml-4">
                                                    <button
                                                        onClick={() => {
                                                            setSelectedPost(post);
                                                            setIsEditModalOpen(true);
                                                        }}
                                                        className="p-2 hover:bg-gray-100 rounded-full"
                                                        title="Edit post"
                                                    >
                                                        <PencilIcon size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeletePost(post.Id)}
                                                        className="p-2 hover:bg-red-100 rounded-full text-red-600"
                                                        title="Delete post"
                                                    >
                                                        <TrashIcon size={16} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>

            <EditBlogPostModal
                isOpen={isCreateModalOpen || isEditModalOpen}
                onClose={() => {
                    setIsCreateModalOpen(false);
                    setIsEditModalOpen(false);
                    setSelectedPost(null);
                }}
                post={selectedPost}
                onSubmit={handlePostSubmit}
                isCreating={isCreateModalOpen}
            />
        </div>
    );
} 